import {
  ApiParticipantChartAxisType,
  ApiParticipantChartSettingAxisConfig,
  ApiPerformanceReview,
  ApiPerformanceReviewParticipantChartSetting,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { useSearchParams } from "react-router-dom";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PerformanceReviewInsightConfigureChartModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewInsightConfigureChartModal.tsx";
import { Button, SelectItem, useModals } from "@skailer-com/uikit";
import { filtersLibrary } from "@/components/filter/filter.types.ts";

export interface AxisOption extends SelectItem {
  type: ApiParticipantChartAxisType;
  reviewer_type_id: number | null;
}

export interface useChartSettingProps {
  review: ApiPerformanceReview;
}

export const useChartSetting = ({ review }: useChartSettingProps) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const [searchParams, setSearchParams] = useSearchParams();
  const library = filtersLibrary();

  const parseAndValidateParam = (param: string) => {
    function parseChartAxis(
      value: string | null
    ): ApiParticipantChartSettingAxisConfig | undefined {
      if (!value) return undefined;
      if (value === ApiParticipantChartAxisType.overallReview) {
        return { type: ApiParticipantChartAxisType.overallReview, reviewer_type_id: null };
      }
      const match = value.match(/^reviewer-type-(\d+)$/);
      if (match) {
        return {
          type: ApiParticipantChartAxisType.reviewerType,
          reviewer_type_id: Number(match[1]),
        };
      }
      return undefined;
    }

    const parsed = parseChartAxis(searchParams.get(param));
    const isValid = options.some(
      (opt) => parsed?.type === opt.type && parsed?.reviewer_type_id === opt.reviewer_type_id
    );
    return isValid && parsed
      ? parsed
      : {
          type: options[0].type,
          reviewer_type_id: options[0].reviewer_type_id,
        };
  };

  const options = useMemo((): AxisOption[] => {
    return [
      {
        id: ApiParticipantChartAxisType.overallReview,
        title: t("performance_review:overall"),
        type: ApiParticipantChartAxisType.overallReview,
        reviewer_type_id: null,
      },
      ...review.reviewer_types.map((x) => ({
        id: `reviewer-type-${x.id}`,
        title:
          PerformanceReviewReviewerTypeItems().find((item) => item.id === x.type)?.title ??
          x.title ??
          "",
        type: ApiParticipantChartAxisType.reviewerType,
        reviewer_type_id: x.id,
      })),
    ];
  }, [review]);

  const setting = useMemo(() => {
    return {
      x_axis: parseAndValidateParam("x_axis"),
      y_axis: parseAndValidateParam("y_axis"),
    } as ApiPerformanceReviewParticipantChartSetting;
  }, [searchParams, options]);

  const handleOpenConfigureChartModal = () => {
    openModal(PerformanceReviewInsightConfigureChartModal, {
      setting,
      options,
      onSubmit: (newSetting) => {
        const encode = (axis: ApiParticipantChartSettingAxisConfig) =>
          axis.type === ApiParticipantChartAxisType.overallReview
            ? axis.type
            : `reviewer-type-${axis.reviewer_type_id}`;

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("x_axis", encode(newSetting.x_axis));
          next.set("y_axis", encode(newSetting.y_axis));
          next.delete(library.employee_ids.name);
          next.delete(library.text.searchParamName);
          return next;
        });
      },
    });
  };

  const settingButton = () => {
    return (
      <Button onClick={handleOpenConfigureChartModal} variant="secondary">
        {t("performance_review:configure_chart")}
      </Button>
    );
  };

  return { setting, settingButton };
};
